import type { RealtimeChannel } from "@supabase/supabase-js";
import { getSupabase } from "../auth/supabaseClient";

// ── Types ────────────────────────────────────────────────────────────────────

export type MessageType =
  | "system"
  | "friend_request"
  | "reward"
  | "promo"
  | "navigate"
  | "link";

export type ActionType =
  | "accept_decline"
  | "claim_reward"
  | "navigate"
  | "external_link"
  | "dismiss";

export interface InboxMessage {
  id: string;
  type: MessageType;
  senderId: string | null;
  senderName: string;
  senderSkin: string;
  senderLevel: number;
  body: string;
  accentColor: string;
  actionType: ActionType | null;
  actionPayload: Record<string, unknown> | null;
  createdAt: string;
  read: boolean;
  virtual: boolean;
}

// ── Constants ────────────────────────────────────────────────────────────────

const TYPE_COLORS: Record<MessageType, string> = {
  system: "#ffd700",
  friend_request: "#4dff88",
  reward: "#ff6b9d",
  promo: "#64d8ff",
  navigate: "#a78bfa",
  link: "#ff9944",
};

// ── Service ──────────────────────────────────────────────────────────────────

export class MessageService {
  private userId: string | null = null;
  private userCreatedAt: string | null = null;
  private inbox: InboxMessage[] = [];
  private targetedChannel: RealtimeChannel | null = null;
  private broadcastChannel: RealtimeChannel | null = null;

  /** Optional callback invoked when a new message arrives via Realtime. */
  onNewMessage: ((msg: InboxMessage) => void) | null = null;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  init(userId: string, userCreatedAt?: string): void {
    // Guard against double-init (auth fires multiple times)
    if (this.userId === userId && this.broadcastChannel != null) {
      return;
    }
    this.unsubscribeRealtime(); // clean up any previous channels
    this.userId = userId;
    this.userCreatedAt = userCreatedAt ?? null;
    void this.fetchInbox();
    this.subscribeRealtime(userId);
  }

  destroy(): void {
    this.unsubscribeRealtime();
    this.userId = null;
    this.inbox = [];
    this.onNewMessage = null;
  }

  // ── Inbox fetch ──────────────────────────────────────────────────────────

  async fetchInbox(): Promise<InboxMessage[]> {
    if (this.userId == null) return [];
    const supabase = getSupabase();

    // Build query: targeted messages for this user + broadcasts created after the user signed up.
    // This prevents new users from seeing a backlog of old broadcast announcements.
    let query = supabase
      .from("messages")
      .select(`
        id, type, sender_id, recipient_id, title, body, accent_color,
        action_type, action_payload, expires_at, created_at,
        sender:profiles!messages_sender_id_fkey(username, level, equipped_character),
        message_reads!left(read_at)
      `)
      .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`);

    if (this.userCreatedAt != null) {
      // Targeted messages (any time) OR broadcasts only after user signup
      query = query.or(
        `recipient_id.eq.${this.userId},and(recipient_id.is.null,created_at.gte.${this.userCreatedAt})`,
      );
    } else {
      query = query.or(`recipient_id.eq.${this.userId},recipient_id.is.null`);
    }

    const { data, error } = await query
      .order("created_at", { ascending: false })
      .limit(50);

    if (error || data == null) {
      console.warn("[messages] fetchInbox failed:", error?.message);
      return this.inbox;
    }

    this.inbox = (data as Record<string, unknown>[]).map((row) => this.rowToMessage(row));

    return this.inbox;
  }

  // ── Read tracking ────────────────────────────────────────────────────────

  async markRead(messageId: string): Promise<void> {
    // Update cached state immediately
    const msg = this.inbox.find((m) => m.id === messageId);
    if (msg == null || msg.read) return;
    msg.read = true;

    // Skip DB for virtual messages (e.g. friend requests)
    if (msg.virtual) return;

    if (this.userId == null) return;
    const supabase = getSupabase();
    const { error } = await supabase.from("message_reads").upsert(
      { user_id: this.userId, message_id: messageId, read_at: new Date().toISOString() },
      { onConflict: "user_id,message_id" },
    );
    if (error) {
      console.warn("[messages] markRead failed:", error.message);
    }
  }

  // ── Friend-request merge ────────────────────────────────────────────────

  mergeFriendRequests(
    pending: { odId: string; username: string; level: number }[],
    readIds: Set<string>,
  ): void {
    // Remove stale virtual friend_request entries
    this.inbox = this.inbox.filter(
      (m) => !(m.virtual && m.type === "friend_request"),
    );

    for (const req of pending) {
      const virtualId = `fr_${req.odId}`;
      this.inbox.unshift({
        id: virtualId,
        type: "friend_request",
        senderId: req.odId,
        senderName: req.username,
        senderSkin: "skin_001",
        senderLevel: req.level,
        body: `${req.username} wants to be your friend!`,
        accentColor: TYPE_COLORS.friend_request,
        actionType: "accept_decline",
        actionPayload: { friendUserId: req.odId },
        createdAt: new Date().toISOString(),
        read: readIds.has(virtualId),
        virtual: true,
      });
    }
  }

  // ── Cached accessors ────────────────────────────────────────────────────

  getCachedInbox(): InboxMessage[] {
    return this.inbox;
  }

  getUnreadCount(): number {
    return this.inbox.filter((m) => !m.read).length;
  }

  getMessageById(id: string): InboxMessage | undefined {
    return this.inbox.find((m) => m.id === id);
  }

  // ── Realtime ─────────────────────────────────────────────────────────────

  private subscribeRealtime(userId: string): void {
    const supabase = getSupabase();

    this.targetedChannel = supabase
      .channel("messages:targeted")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
          filter: `recipient_id=eq.${userId}`,
        },
        (payload) => void this.handleRealtimeInsert(payload.new as Record<string, unknown>),
      )
      .subscribe();

    this.broadcastChannel = supabase
      .channel("messages:broadcast")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
        },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          // Only handle broadcasts (null recipient) or messages for this user
          // Targeted messages are already handled by the other channel
          if (row.recipient_id != null && row.recipient_id !== userId) return;
          if (row.recipient_id === userId) return; // handled by targeted channel
          void this.handleRealtimeInsert(row);
        },
      )
      .subscribe((_status) => {
      });
  }

  private unsubscribeRealtime(): void {
    const supabase = getSupabase();
    if (this.targetedChannel != null) {
      void this.targetedChannel.unsubscribe();
      void supabase.removeChannel(this.targetedChannel);
      this.targetedChannel = null;
    }
    if (this.broadcastChannel != null) {
      void this.broadcastChannel.unsubscribe();
      void supabase.removeChannel(this.broadcastChannel);
      this.broadcastChannel = null;
    }
  }

  private async handleRealtimeInsert(row: Record<string, unknown>): Promise<void> {
    // Fetch sender profile so we have name/skin/level
    const senderId = row.sender_id as string | null;
    let senderName = "System";
    let senderSkin = "skin_001";
    let senderLevel = 0;

    if (senderId != null) {
      const supabase = getSupabase();
      const { data: profile } = await supabase
        .from("profiles")
        .select("username, level, equipped_character")
        .eq("id", senderId)
        .single();

      if (profile != null) {
        senderName = (profile as { username: string | null }).username ?? "???";
        senderLevel = (profile as { level: number }).level ?? 1;
        senderSkin = (profile as { equipped_character: string | null }).equipped_character ?? "skin_001";
      }
    }

    const msg: InboxMessage = {
      id: row.id as string,
      type: (row.type as MessageType) ?? "system",
      senderId,
      senderName,
      senderSkin,
      senderLevel,
      body: (row.body as string) ?? "",
      accentColor: (row.accent_color as string) ?? TYPE_COLORS[(row.type as MessageType) ?? "system"],
      actionType: (row.action_type as ActionType) ?? null,
      actionPayload: (row.action_payload as Record<string, unknown>) ?? null,
      createdAt: (row.created_at as string) ?? new Date().toISOString(),
      read: false,
      virtual: false,
    };

    // Prepend to inbox (newest first)
    this.inbox.unshift(msg);

    if (this.onNewMessage != null) {
      this.onNewMessage(msg);
    }
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  private rowToMessage(row: Record<string, unknown>): InboxMessage {
    const sender = row.sender as { username: string | null; level: number; equipped_character: string | null } | null;
    const reads = row.message_reads as { read_at: string | null }[] | null;
    const msgType = (row.type as MessageType) ?? "system";

    return {
      id: row.id as string,
      type: msgType,
      senderId: (row.sender_id as string) ?? null,
      senderName: sender?.username ?? "System",
      senderSkin: sender?.equipped_character ?? "skin_001",
      senderLevel: sender?.level ?? 0,
      body: (row.body as string) ?? "",
      accentColor: (row.accent_color as string) ?? TYPE_COLORS[msgType],
      actionType: (row.action_type as ActionType) ?? null,
      actionPayload: (row.action_payload as Record<string, unknown>) ?? null,
      createdAt: (row.created_at as string) ?? "",
      read: reads != null && reads.length > 0 && reads[0].read_at != null,
      virtual: false,
    };
  }
}
